import { TFunction } from 'i18next';
import moment from 'moment-timezone';

/** Ellipsize a string if it exceeds @maxLength
 *
 * @param value The string to possibly ellipsize
 * @param maxLength The maximum length of the string
 */
export const ellipsize = (value: string, maxLength: number) => {
	if (value && value.length > maxLength) {
		return value.slice(0, maxLength) + '...';
	}
	return value;
};

/** Get a logical string representation of a given duration, e.g. 20h 30min
 *
 * @param durationInSeconds The duration in seconds
 * @param t The i18next translation function
 */
export const getDurationString = (durationInSeconds: number, t: TFunction) => {
	if (durationInSeconds < 60) {
		return '--';
	}

	if (durationInSeconds < 3600) {
		const minutes = Math.round(durationInSeconds / 60);
		return t('common:durations.minutesShort', {
			minutes,
			defaultValue: '{{minutes}}min',
		});
	}

	if (durationInSeconds < 3600 * 24) {
		const hours = Math.floor(durationInSeconds / 3600);
		const minutes = Math.round((durationInSeconds - hours * 3600) / 60);
		return t('common:durations.hoursAndMinutesShort', {
			hours,
			minutes,
			defaultValue: '{{hours}}h {{minutes}}m',
		});
	}

	const days = Math.round(durationInSeconds / (3600 * 24));
	const dayLocale = days === 1 ? t('common:times.day') : t('common:times.days');
	return `${days} ${dayLocale}`;
};

/** Format a date as relative to now, e.g. 18:00 tomorrow, if applicable
 *
 * @param date the date to format
 */
export const getRelativeTimeString = (date: moment.Moment, t: TFunction): string => {
	const now = moment();

	if (date.isBefore(now.clone().subtract(1, 'days').startOf('day'))) {
		return date.format('LT, dddd [(]DD.MM[)]');
	}
	if (date.isBefore(now.clone().subtract(1, 'days').endOf('day'))) {
		return date.format(`LT [${t('common:times.yesterday', 'Yesterday')}]`);
	}
	if (date.isBefore(now.clone().endOf('day'))) {
		return date.format(`LT [${t('common:times.today', 'Today')}]`);
	}
	if (date.isBefore(now.clone().add(1, 'days').endOf('day'))) {
		return date.format(`LT [${t('common:times.tomorrow', 'Tomorrow')}]`);
	}

	return date.format('LT, dddd [(]DD.MM[)]');
};
